// Chemin: C:\gestion-copro\src\types\onboarding.types.ts
// Types pour l'onboarding conversationnel SENTINELLE VIGIE
// Session 108 : Chantier 0 — "Bonjour Nicole, vous n'êtes plus seule"
// Session 109 : Corrections — tantièmes (numérateur + dénominateur), statut occupation,
//               localisation malfaçons, adresse postale, création interlocuteurs auto
// Session 159 : Ajout étape infos_copropriete (code postal, commune, nb lots, RNC)
// Session 166 : Ajout CleRepartition, enrichissement OnboardingLot (numéro, étage, clés détaillées)

import { Timestamp } from 'firebase/firestore';

// ============================================
// DONNÉES COLLECTÉES PENDANT L'ONBOARDING
// ============================================

/** Relation avec le syndic */
export type RelationSyndic = 'ok' | 'lent' | 'problemes' | 'nouveau';

/** État du conseil syndical */
export type EtatCS = 'actif' | 'inactif' | 'absent' | 'inconnu';

/** Nature d'un lot */
export type NatureLot = 'appartement' | 'parking' | 'cave' | 'garage' | 'commerce' | 'autre';

/** Statut d'occupation — Session 109 */
export type StatutOccupation = 'occupant' | 'bailleur' | 'mixte';

/** Localisation des malfaçons — Session 109 */
export type LocalisationMalfacons = 'parties_communes' | 'lot_privatif' | 'les_deux';

// ============================================
// CLÉS DE RÉPARTITION DES TANTIÈMES (Session 166)
// Référence : CADRAGE_CHARGES_RECUPERABLES_BAILLEUR.md
// ============================================

/** Clé de répartition des tantièmes — une par poste de charges */
export interface CleRepartition {
  /** Nom de la clé (ex: "Charges générales", "Bâtiment", "Ascenseur") */
  nom: string;
  /** Code court (ex: "CCG", "BA", "ASC", "LOG") */
  code: string;
  /** Tantièmes du lot pour cette clé */
  tantiemes: number;
  /** Dénominateur (ex: 10000) */
  denominateur: number;
}

/** Lot saisi pendant l'onboarding — Session 109 : tantièmes en numérateur, Session 166 : enrichissement */
export interface OnboardingLot {
  nature: NatureLot;
  /** Numéro du lot (ex: 3) — optionnel, extrait du règlement ou saisi */
  numeroLot?: number;
  /** Étage du lot — important pour ascenseur (RDC = 0 tantièmes). null = inconnu */
  etage?: number | 'rdc' | null;
  /** Tantièmes simples (onboarding) — ratio unique, approximatif. null = "je ne sais pas" */
  tantiemes?: number | null;
  /** Clés de répartition détaillées (extraction règlement) — précis, poste par poste */
  clesRepartition?: CleRepartition[];
}

/** Adresse postale du copropriétaire — Session 109 */
export interface AdressePostale {
  rue: string;
  codePostal: string;
  ville: string;
}

/**
 * Session 159 : Informations de la copropriété (distinctes de l'adresse du copropriétaire)
 * Collectées en début d'onboarding pour identifier précisément la copropriété
 */
export interface InfosCopropriete {
  /** Nom de la copropriété (pré-rempli depuis inscription) */
  nomCopropriete: string;
  /** Adresse de la copropriété (rue / n° voie) */
  adresseCopropriete: string;
  /** Code postal — OBLIGATOIRE */
  codePostal: string;
  /** Commune — OBLIGATOIRE */
  commune: string;
  /** Nombre total de lots de la copropriété (optionnel) */
  nombreLotsTotal?: number;
  /** Numéro d'immatriculation au Registre National des Copropriétés (optionnel) */
  numeroRNC?: string;
}

/** Garanties calculées automatiquement à partir de la date de livraison */
export interface GarantiesVEFA {
  /** Garantie de parfait achèvement — 1 an après livraison */
  gpa: string;
  /** Garantie biennale — 2 ans après livraison */
  biennale: string;
  /** Garantie décennale — 10 ans après livraison */
  decennale: string;
}

/** Données collectées pendant l'onboarding — persistées dans Firestore */
export interface OnboardingData {
  /** Date de complétion de l'onboarding */
  dateReponses: Timestamp | null;
  /** Copropriété de moins de 10 ans ? */
  coproprieteNeuve: boolean | null;
  /** Session 159 : Informations copropriété (nom, adresse, CP, commune, nb lots, RNC) */
  infosCopropriete?: InfosCopropriete;
  /** Date de livraison (format "YYYY-MM") — uniquement si neuve */
  dateLivraison?: string;
  /** Garanties calculées — uniquement si neuve */
  garanties?: GarantiesVEFA;
  /** Nom du syndic */
  nomSyndic?: string;
  /** Qualité de la relation avec le syndic */
  relationSyndic?: RelationSyndic;
  /** Nom du promoteur — uniquement si neuve */
  nomPromoteur?: string;
  /** État du conseil syndical */
  csActif?: EtatCS;
  /** Lots du copropriétaire (avec tantièmes optionnels — Session 109, enrichis Session 166) */
  lots?: OnboardingLot[];
  /** Total tantièmes de la copropriété — dénominateur (ex: 10000) — Session 109 */
  totalTantiemes?: number | null;
  /** Statut d'occupation — Session 109 */
  statutOccupation?: StatutOccupation;
  /** Adresse postale — Session 109 */
  adressePostale?: AdressePostale;
  /** Malfaçons signalées ? */
  malfacons?: boolean;
  /** Localisation des malfaçons — Session 109 */
  localisationMalfacons?: LocalisationMalfacons;
}

// ============================================
// ÉTAPES DE L'ONBOARDING
// ============================================

/** Identifiant de chaque étape du parcours */
export type OnboardingStepId =
  // Phase 1 — Accueil
  | 'accueil'
  // Phase 2 — Situation
  | 'infos_copropriete'       // Session 159 — NOUVEAU : nom, adresse, CP, commune, nb lots, RNC
  | 'type_copro'
  | 'date_livraison'
  | 'garanties_calculees'
  | 'nom_syndic'
  | 'relation_syndic'
  | 'nom_promoteur'
  | 'cs_actif'
  | 'lots'
  | 'statut_occupation'       // Session 109
  | 'adresse_postale'         // Session 109
  | 'malfacons'
  | 'localisation_malfacons'  // Session 109
  // Phase 3 — Diagnostic
  | 'synthese'
  // Phase 4 — Premier document
  | 'premier_document'
  // Phase 5 — Plan d'action
  | 'plan_action';

/** Phase de l'onboarding (pour affichage progression) */
export type OnboardingPhase = 1 | 2 | 3 | 4 | 5;

/** Mapping étape → phase */
export const STEP_TO_PHASE: Record<OnboardingStepId, OnboardingPhase> = {
  accueil: 1,
  infos_copropriete: 2,       // Session 159
  type_copro: 2,
  date_livraison: 2,
  garanties_calculees: 2,
  nom_syndic: 2,
  relation_syndic: 2,
  nom_promoteur: 2,
  cs_actif: 2,
  lots: 2,
  statut_occupation: 2,
  adresse_postale: 2,
  malfacons: 2,
  localisation_malfacons: 2,
  synthese: 3,
  premier_document: 4,
  plan_action: 5,
};

/** Labels des phases pour l'indicateur de progression */
export const PHASE_LABELS: Record<OnboardingPhase, string> = {
  1: 'Accueil',
  2: 'Ma situation',
  3: 'Diagnostic',
  4: 'Premier document',
  5: 'Plan d\'action',
};

// ============================================
// MESSAGES SENTINELLE (PRÉ-ÉCRITS)
// ============================================

/** Type d'un message dans le fil conversationnel */
export type OnboardingMessageRole = 'sentinelle' | 'utilisateur';

/** Un message affiché dans le fil */
export interface OnboardingMessage {
  id: string;
  role: OnboardingMessageRole;
  contenu: string;
  timestamp: number;
}

// ============================================
// TYPE D'INPUT ATTENDU PAR ÉTAPE
// ============================================

/** Bouton de choix */
export interface OnboardingChoix {
  label: string;
  value: string;
  description?: string;
}

/** Configuration d'une réponse utilisateur */
export type OnboardingInputType =
  | { type: 'boutons'; choix: OnboardingChoix[] }
  | { type: 'date_mois_annee' }
  | { type: 'texte_libre'; placeholder: string }
  | { type: 'lots_picker' }
  | { type: 'adresse_postale' }
  | { type: 'infos_copropriete' }  // Session 159
  | { type: 'aucun' }
  | { type: 'document_upload' };

// ============================================
// ÉTAT COMPLET DE L'ONBOARDING
// ============================================

/** État persisté dans Firestore sur le document copropriete */
export interface OnboardingState {
  /** Onboarding terminé ? */
  onboardingComplete: boolean;
  /** Étape courante (pour reprise si interruption) */
  onboardingStep: OnboardingStepId;
  /** Données collectées */
  onboardingData: OnboardingData;
}

/** Valeur initiale à l'inscription */
export const ONBOARDING_INITIAL_STATE: OnboardingState = {
  onboardingComplete: false,
  onboardingStep: 'accueil',
  onboardingData: {
    dateReponses: null,
    coproprieteNeuve: null,
  },
};
